import { useState } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';

import { UserRole } from '@/api-models/UserRole';
import { useAuth } from '@/contexts/auth-context';
import classNames from '@/utils/classnames';

import type { ToolCallState } from '../../stores/agent-session-store';
import { parseTodos } from '../../stores/todos';

import { BrowserToolCard } from './BrowserToolCard';
import { TodoToolCard } from './TodoToolCard';
import { summarizeToolInput } from './tool-metadata';
import { ToolCallHeader } from './ToolCallHeader';

function prettyInput(input: unknown): string {
  if (input === null || input === undefined) return '';
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

export function ToolCallRow({ toolCall }: { toolCall: ToolCallState }) {
  const { me } = useAuth();
  // Platform-level Zooid-staff gate (same check as AdminGuard) — NOT the team-level space admin.
  const isStaff = me.role === UserRole.Admin;
  const [debugOpen, setDebugOpen] = useState(false);

  // Both bespoke cards render the call's *arguments*, which a pending row does not have yet — so
  // they only take over once the call is confirmed. Until then it shows as the generic row.
  if (!toolCall.pending) {
    // The set_todos tool gets a bespoke checklist rather than the generic row — unless the agent
    // loop refused the list, which then never became the plan and shows as the failed call it is.
    if (toolCall.name === 'set_todos' && !toolCall.isError) {
      const todos = parseTodos(toolCall.input);
      if (todos) return <TodoToolCard todos={todos} />;
    }

    // A screenshot's output is the picture; the generic row would hide it behind the staff gate.
    if (toolCall.name === 'browser_screenshot') {
      return <BrowserToolCard toolCall={toolCall} />;
    }
  }

  const rawInput = prettyInput(toolCall.input);
  // A pending row has no arguments to summarise, so it says what it is waiting for instead of
  // sitting blank next to a spinner — for a `write` this is the longest part of the call.
  const summary = toolCall.pending ? 'receiving…' : summarizeToolInput(toolCall.name, toolCall.input);

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2">
      <ToolCallHeader toolCall={toolCall} summary={summary}>
        {isStaff && (
          <button
            type="button"
            onClick={() => setDebugOpen((o) => !o)}
            aria-expanded={debugOpen}
            title="Show raw tool input and output"
            className={classNames(
              'ml-auto flex shrink-0 items-center rounded-sm p-0.5 transition-colors',
              debugOpen ? 'text-fg-secondary' : 'text-fg-tertiary hover:text-fg-secondary',
            )}
          >
            <CaretDownIcon size={12} className={classNames('transition-transform', debugOpen && 'rotate-180')} />
          </button>
        )}
      </ToolCallHeader>
      {isStaff && debugOpen && (
        <div className="flex flex-col gap-1.5 pt-1">
          {rawInput && (
            <div className="flex flex-col gap-0.5">
              <span className="text-2xs font-medium uppercase tracking-wide text-fg-tertiary">Input</span>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-surface-2 px-2 py-1.5 font-mono text-2xs text-fg-secondary">
                {rawInput}
              </pre>
            </div>
          )}
          {toolCall.output && (
            <div className="flex flex-col gap-0.5">
              <span className="text-2xs font-medium uppercase tracking-wide text-fg-tertiary">Output</span>
              <pre
                className={classNames(
                  'max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-surface-2 px-2 py-1.5 font-mono text-2xs',
                  toolCall.isError ? 'text-danger' : 'text-fg-secondary',
                )}
              >
                {toolCall.output}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
